<template>
  <v-layout
    id="course-form"
    wrap
  >
    <helper-cropper
      ref="cropper"
      :aspect-ratio="300 / 200"
      :width="300"
      :height="200"
      @updateAvatar="updateImage"
    />
    <v-flex
      mt-4
      xs12
    >
      <div
        class="image"
        :style="{'border-color': form.bg_color, 'border-width': form.bg_color ? '3px' : '0px'}"
        @click.stop="$refs.image.click()"
      >
        <div
          v-if="!form.image"
          class="pt-5 pb-7"
        >
          <v-icon color="primary">
            mdi-cloud-upload
          </v-icon>
          <p>
            {{ $t('modules.courseUploadImage') }}
          </p>
          <p>PNG / JPG</p>
        </div>
        <div style="width:300px;overflow:hidden;">
          <img
            v-if="form.image"
            :src="form.image"
            width="300"
          >
        </div>
        <input
          ref="image"
          type="file"
          style="display: none"
          accept="image/*"
          @change="setImage"
        >
      </div>
      <div class="text-center mt-2">
        <v-menu
          offset-y
          right
          bottom
          :close-on-content-click="false"
        >
          <template v-slot:activator="{ on }">
            <v-btn
              outlined
              small
              text
              v-on="on"
            >
              <v-icon>
                mdi-border-all-variant
              </v-icon>
            </v-btn>
          </template>
          <v-color-picker
            v-model="pickedColor"
            elevation="1"
            @update:color="form.bg_color = pickedColor"
          />
        </v-menu>
        <v-btn
          v-if="form.bg_color"
          class="ml-2"
          outlined
          small
          text
          @click="form.bg_color = ''"
        >
          <v-icon>
            mdi-select-off
          </v-icon>
        </v-btn>
        <v-btn
          v-if="form.image"
          class="ml-2"
          outlined
          small
          text
          @click="form.image = ''; $refs.image.value = '';"
        >
          <v-icon>
            mdi-bucket-outline
          </v-icon>
        </v-btn>
      </div>
    </v-flex>
    <v-flex
      xs12
    >
      <v-text-field
        v-model.trim="form.name"
        :error-messages="NameErrors"
        :label="$t('modules.courseName')+ '*'"
        required
        @input="$v.form.name.$touch()"
        @blur="$v.form.name.$touch()"
      />
    </v-flex>
    <v-flex
      xs12
    >
      <v-select
        v-model.trim="form.status"
        :items="statuses"
        :item-text="'text'"
        :item-value="'id'"
        :error-messages="StatusErrors"
        :label="$t('modules.courseStatus')+'*'"
      />
    </v-flex>
    <v-flex
      xs12
    >
      <v-checkbox
        v-model="form.available_for_all"
        color="primary"
        :label="$t('modules.courseEnroll')"
      />
    </v-flex>
    <v-flex
      v-if="!form.available_for_all"
      xs12
    >
      <v-select
        v-model="form.product_id"
        multiple
        :items="moduleProducts"
        :item-text="'name'"
        :item-value="'id'"
        :error-messages="ProductsErrors"
        :label="$t('modules.courseSelectProduct')+'*'"
        @input="$v.form.product_id.$touch()"
        @blur="$v.form.product_id.$touch()"
      />
    </v-flex>
    <v-flex
      xs12
    >
      <b>{{ $t("modules.courseShortDescription") }}</b>
    </v-flex>
    <v-flex
      xs12
    >
      <v-textarea
        v-model.trim="form.excerpt"
        outlined
        :error-messages="ExcerptErrors"
        no-resize
        @input="$v.form.excerpt.$touch()"
        @blur="$v.form.excerpt.$touch()"
      />
    </v-flex>
    <v-flex
      xs12
    >
      <b>{{ $t("modules.courseLongDescription") }}</b>
    </v-flex>
    <v-flex>
      <vue-editor
        id="editor"
        v-model="form.description"
        :editor-toolbar="customToolbar"
        :editor-options="editorSettings"
      />
    </v-flex>
    <v-flex xs12>
      <v-divider />
    </v-flex>
    <v-flex
      xs12
    >
      <v-btn
        fab
        outlined
        small
        text
        @click="$emit('closeCourseForm', false)"
      >
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-btn
        color="primary"
        style="float:right"
        :disabled="loading || $v.$invalid"
        :loading="loading"
        @click="save()"
      >
        {{ $t('globals.saveButton') }}
      </v-btn>
    </v-flex>
  </v-layout>
</template>
<script>
import PRODUCTS_QUERY from "@/graphql/queries/modules/Products.gql";
import COURSE_CREATE_MUTATION from "@/graphql/mutations/modules/CourseCreate.gql";
import COURSE_UPDATE_MUTATION from "@/graphql/mutations/modules/CourseUpdate.gql";
import { validationMixin } from "vuelidate";
import {
  required,
  minLength,
  maxLength,
  requiredIf
} from "vuelidate/lib/validators";
import { VueEditor } from "vue2-editor";

export default {
  name: "CourseLabelForm",
  components: {
    VueEditor
  },
  mixins: [validationMixin],
  props: {
    course: {
      type: Object,
      default: null
    },
    whitelabelUuid: {
      type: String,
      default: null
    },
    courseUuid: {
      type: String,
      default: null
    },
  },
  data() {
    return {
      loading: false,
      pickedColor: "",
      statuses: [
        {id: "active", text: this.$t('modules.courseStatusActive')},
        {id: "archived", text: this.$t('modules.courseStatusInactive')}
      ],
      customToolbar: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'script': 'sub'}, { 'script': 'super' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'direction': 'rtl' }],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': []}, 'link', 'image'],

        ['clean']
      ],
      editorSettings: {
        modules: {
          imageResize: {
            displaySize: true,
            imageDrop: true
          }
        }
      },
      form: {}
    }
  },
  computed: {
    rules() {
      return {
        name: {
          required,
          minLength: minLength(3),
          maxLength: maxLength(100),
        },
        status: {
         required,
        },
        available_for_all: {
          required,
        },
        product_id: {
          required: requiredIf(function() {
              return !this.form.available_for_all;
          }),
        },
        excerpt: {
          minLength: minLength(3),
          maxLength: maxLength(200),
        },
      }
    },
    NameErrors() {
      const errors = [];
      if (!this.$v.form.name.$dirty) return errors;
      !this.$v.form.name.required && errors.push(this.$t("profile.reqField"));
      !this.$v.form.name.minLength &&
        errors.push(this.$t("whitelabel.minError", {char: this.$v.form.name.$params.minLength.min}));
      !this.$v.form.name.maxLength &&
        errors.push(this.$t("whitelabel.maxError", {char: this.$v.form.name.$params.maxLength.max}));
      return errors;
    },
    StatusErrors() {
      const errors = [];
      if (!this.$v.form.status.$dirty) return errors;
      !this.$v.form.status.required && errors.push(this.$t("profile.reqField"));
      return errors;
    },
    ProductsErrors() {
      const errors = [];
      if (!this.$v.form.product_id.$dirty) return errors;
      !this.$v.form.product_id.required && errors.push(this.$t("profile.reqField"));
      return errors;
    },
    ExcerptErrors() {
      const errors = [];
      if (!this.$v.form.excerpt.$dirty) return errors;
      !this.$v.form.excerpt.minLength &&
        errors.push(this.$t("whitelabel.minError", {char: this.$v.form.excerpt.$params.minLength.min}));
      !this.$v.form.excerpt.maxLength &&
        errors.push(this.$t("whitelabel.maxError", {char: this.$v.form.excerpt.$params.maxLength.max}));
      return errors;
    },
  },
  mounted() {
    const form = JSON.parse(JSON.stringify(this.course))
    if (form) {
      if(!form.available_for_all) {
        form.product_id = form.products.map(({id})=>id);
      }
      this.form = form
    } else {
      this.form = {
        bg_color: "",
        image: "",
        name: "",
        status: "",
        available_for_all: true,
        excerpt: "",
        description: "",
        product_id: null
      }
    }
  },
  validations() {
    return {
      form: this.rules
    };
  },
  methods: {
    setImage(e) {
      if (!this.$refs.cropper.setImage(e)) {
        this.$refs.image.value = "";
      }
    },
    updateImage(image) {
      this.form.image = image;
    },
    save() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return
      }

      this.loading = true;
      this.form.whitelabel_uuid = this.whitelabelUuid;
      if(this.form.available_for_all) delete(this.form.product_id)
      if(this.course) {
        this.form.course_uuid = this.form.uuid;
      }
      if(!this.course && this.courseUuid) {
        this.form.course_uuid = this.courseUuid
      }

      this.$apollo
        .mutate({
          mutation: !this.course && !this.courseUuid ? COURSE_CREATE_MUTATION : COURSE_UPDATE_MUTATION,
          variables: this.form
        })
        .then(data => {
          this.loading = false;
          this.$emit('nextStep', data.data.moduleLmsCourseCreate ? data.data.moduleLmsCourseCreate : data.data.moduleLmsCourseUpdate);
        })
        .catch(error => {
          this.errorHandler(error);
          this.loading = false
        });
    }
  },
  apollo: {
    moduleProducts: {
      fetchPolicy: "network-only",
      query() {
        return PRODUCTS_QUERY;
      },
      variables() {
        return {
            whitelabel_uuid: this.whitelabelUuid
          }
      },
      skip() {
        return !this.whitelabelUuid;
      },
    },
  },
}

</script>
<style scoped lang="scss">
#editor {
  height: 400px;
}
#course-form {
  .image {
    width: 306px;
    margin: 0 auto;
    border: 3px solid;
    border-radius: 5px;
    text-align: center;
    cursor: pointer;
    i {
      margin: 5px 0 0px;
      font-size: 26px;
    }
    p {
      margin: 7px 0 0!important;
      &:first-of-type {
        font-weight: 700;
      }
      line-height: 0.85rem;
    }
    small {
      font-size: 11px;
    }
    &.no-border {
      border: 0 !important;
    }
  }
  small {
    font-size: 12px;
    font-weight: 600 !important;
    color: #f55a4e !important;
    caret-color: #f55a4e !important;
  }
}
</style>